import React from 'react';
import {
  Badge,
  Box,
  IconButton,
  Button,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import { FiEye, FiXCircle } from 'react-icons/fi';
import dayjs from 'dayjs';
import { SaktiAccessCodeRequest } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiAccessCodeTableProps {
  loading: boolean;
  requests: SaktiAccessCodeRequest[];
  total: number;
  onViewCode: (request: SaktiAccessCodeRequest) => void;
  onCancelRequest: (id: string) => void;
}

const getStatusColorScheme = (status: string) => {
  switch (status) {
    case 'USED': return 'green';
    case 'PENDING': return 'blue';
    case 'EXPIRED': return 'orange';
    case 'CANCELLED': return 'red';
    default: return 'gray';
  }
};

const SaktiAccessCodeTable: React.FC<SaktiAccessCodeTableProps> = ({
  loading,
  requests,
  total,
  onViewCode,
  onCancelRequest,
}) => {
  return (
    <VStack align="stretch" spacing={4}>
      <Skeleton isLoaded={!loading} borderRadius={SAKTI_UI_SIZE.cardRadius}>
        <Box
          borderRadius={SAKTI_UI_SIZE.cardRadius}
          overflow="hidden"
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
        >
          {/* Desktop Table */}
          <Box overflowX="auto" display={{ base: 'none', lg: 'block' }}>
            <Table variant="simple">
              <Thead bg="customGreen.500">
                <Tr>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>NIP</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Nama Karyawan</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Device</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Status</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Requested At</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Expires In</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {requests.length === 0 ? (
                  <Tr>
                    <Td colSpan={7}>
                      <Text color="gray.500" textAlign="center" py={4} fontSize="sm">
                        Tidak ada data permintaan kode akses
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  requests.map((request) => (
                    <Tr key={request.id}>
                      <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{request.nip}</Td>
                      <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{request.employee_name}</Td>
                      <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{request.device_model}</Td>
                      <Td py={2.5}>
                        <Badge fontSize="0.65rem" colorScheme={getStatusColorScheme(request.status)}>
                          {request.status}
                        </Badge>
                      </Td>
                      <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>
                        {dayjs(request.requested_at).format('DD/MM/YYYY HH:mm')}
                      </Td>
                      <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>
                        {request.status === 'PENDING' ? (
                          <Text color={request.seconds_remaining < 60 ? 'red.500' : 'inherit'}>
                            {Math.floor(request.seconds_remaining / 60)}m {request.seconds_remaining % 60}s
                          </Text>
                        ) : '-'}
                      </Td>
                      <Td py={2.5}>
                        <HStack spacing={1}>
                          <Tooltip label="Lihat Kode" hasArrow>
                            <IconButton
                              aria-label="Lihat kode"
                              size="sm"
                              variant="ghost"
                              icon={<FiEye />}
                              onClick={() => onViewCode(request)}
                              isDisabled={request.status !== 'PENDING'}
                            />
                          </Tooltip>
                          <Tooltip label="Batalkan Request" hasArrow>
                            <IconButton
                              aria-label="Batalkan"
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              icon={<FiXCircle />}
                              onClick={() => onCancelRequest(request.id)}
                              isDisabled={request.status !== 'PENDING'}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>

          {/* Mobile Cards */}
          <Box display={{ base: 'block', lg: 'none' }}>
            {requests.length === 0 ? (
              <Box p={6} textAlign="center">
                <Text color="gray.500" fontSize="sm">
                  Tidak ada data permintaan kode akses
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={0} divider={<Divider />}>
                {requests.map((request) => (
                  <Box key={request.id} p={4} _active={{ bg: 'gray.50' }}>
                    <HStack justify="space-between" mb={2}>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" fontSize="sm">{request.employee_name}</Text>
                        <Text fontSize="xs" color="gray.500">{request.nip}</Text>
                      </VStack>
                      <Badge fontSize="0.6rem" colorScheme={getStatusColorScheme(request.status)}>
                        {request.status}
                      </Badge>
                    </HStack>
                    
                    <VStack align="start" spacing={1} mb={3}>
                      <HStack spacing={1}>
                        <Text fontSize="xs" color="gray.500" w="80px">Device:</Text>
                        <Text fontSize="xs">{request.device_model}</Text>
                      </HStack>
                      <HStack spacing={1}>
                        <Text fontSize="xs" color="gray.500" w="80px">Requested:</Text>
                        <Text fontSize="xs">{dayjs(request.requested_at).format('DD/MM/YYYY HH:mm')}</Text>
                      </HStack>
                      {request.status === 'PENDING' && (
                        <HStack spacing={1}>
                          <Text fontSize="xs" color="gray.500" w="80px">Expires In:</Text>
                          <Text fontSize="xs" fontWeight="bold" color={request.seconds_remaining < 60 ? 'red.500' : 'orange.500'}>
                            {Math.floor(request.seconds_remaining / 60)}m {request.seconds_remaining % 60}s
                          </Text>
                        </HStack>
                      )}
                    </VStack>

                    <HStack justify="flex-end" spacing={2}>
                      <Button
                        size="sm"
                        leftIcon={<FiEye />}
                        variant="outline"
                        colorScheme="green"
                        fontSize="xs"
                        onClick={() => onViewCode(request)}
                        isDisabled={request.status !== 'PENDING'}
                      >
                        Lihat Kode
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<FiXCircle />}
                        variant="ghost"
                        colorScheme="red"
                        fontSize="xs"
                        onClick={() => onCancelRequest(request.id)}
                        isDisabled={request.status !== 'PENDING'}
                      >
                        Batal
                      </Button>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </Box>

          <Box px={4} py={3} bg="customGreen.500" color="white">
            <Text fontSize="xs" fontWeight="semibold" letterSpacing="wider">
              TOTAL {total.toLocaleString('id-ID')} REQUESTS
            </Text>
          </Box>
        </Box>
      </Skeleton>
    </VStack>
  );
};

export default SaktiAccessCodeTable;
